// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-VIEWS-COLUMN-RESIZE (ClickUp 86cb27wub) — Excel-style column resizing, shared by every view
// renderer (StationQueue, CustomViewTab, RefillsTab) so all views behave identically and a new
// view inherits it by construction.
//
// HOW THE WIDTHS ACTUALLY APPLY. A <col width> is only binding under `table-layout: fixed`; under
// the default `auto` layout the browser treats it as a suggestion and content wider than the width
// wins — which is exactly what "make this column narrower" is fighting. But switching a table to
// fixed layout with only SOME columns sized makes the browser divide the remaining space equally,
// which would visibly reshuffle every list on first paint.
//
// So: on mount we MEASURE the columns the browser already laid out, keep those as the baseline,
// and only then switch to fixed layout. Same pixels as before the change; user overrides then sit
// on top. Measurement is in a ref-backed state (not persisted) — only deliberate user resizes are
// written to storage, so a browser/zoom/font difference never gets baked into the saved prefs.
//
// Persistence is DURABLE (localStorage, per view key): widths are UI preferences with no patient
// content, so unlike sticky filters they should outlive the tab. See useDurableState.
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useDurableState } from '../useStickyState'

export const MIN_COL_PX = 60      // a column can never vanish (design: ~60px floor)
export const MAX_COL_PX = 1200    // and never swallow the viewport

export type ColumnWidths = Record<string, number>

export interface ColumnResizeApi {
  /** Attach to the <table> so the hook can measure the browser's own layout once. */
  tableRef: React.RefObject<HTMLTableElement>
  /** Width to render for a column's <col>, or undefined before the first measurement. */
  widthFor: (colKey: string) => number | undefined
  /** 'fixed' once measured — before that 'auto', so the first paint is the browser's own layout. */
  tableLayout: 'auto' | 'fixed'
  /** Live drag: set without persisting (persist happens on release). */
  dragTo: (colKey: string, px: number) => void
  /** Persist the current widths (call on pointer release). */
  commit: () => void
  /** Double-click: size the column to its widest rendered cell. */
  autoFit: (colKey: string, colIndex: number) => void
  /**
   * Re-measure every column from the CURRENT layout. Called at the start of a drag so the
   * baseline comes from the table as it is on screen, showing real rows — never from the
   * skeleton-row layout that happened to be up when the column set first resolved.
   */
  captureBaseline: () => void
  /**
   * Class for the <table> when widths are binding. Under `table-layout: fixed` a cell wider
   * than its column SPILLS OVER its neighbour, so cells must clip — see `.cols-fixed` in
   * index.css. Empty string while layout is auto.
   */
  tableClass: string
  /**
   * Sum of the current column widths, or undefined before measurement.
   *
   * The table MUST be sized to this once widths are applied. A table pinned to a fixed
   * `minWidth` (StationQueue used 2200px) keeps that width under `table-layout: fixed`, so the
   * browser redistributes whatever pixels a drag removes back across the other columns and the
   * resize appears to do nothing at all. Sizing the table to the sum lets the removed pixels
   * actually leave the layout; the scroll wrapper takes care of the overflow.
   */
  totalWidth: number | undefined
  /** True when the user has resized anything on this view — drives the reset affordance. */
  hasCustomWidths: boolean
  /** Drop every stored width for this view and return to the measured baseline. */
  resetWidths: () => void
}

const clamp = (px: number) => Math.max(MIN_COL_PX, Math.min(MAX_COL_PX, Math.round(px)))

/**
 * @param viewKey  stable per view — e.g. `sq:all` or `cv:<viewId>`. null disables persistence.
 * @param colKeys  the currently visible column keys, in render order.
 */
export function useColumnWidths(viewKey: string | null, colKeys: string[]): ColumnResizeApi {
  const tableRef = useRef<HTMLTableElement>(null)
  const [saved, setSaved] = useDurableState<ColumnWidths>(viewKey ? `${viewKey}:colWidths` : null, {})
  // Live (un-persisted) widths during a drag, plus the measured baseline.
  const [live, setLive] = useState<ColumnWidths>({})
  const [measured, setMeasured] = useState<ColumnWidths>({})
  const signature = colKeys.join('|')

  // Measure once per column set. useLayoutEffect so the fixed-layout switch happens before paint —
  // with useEffect the user would see one frame of auto layout, then a jump.
  useLayoutEffect(() => {
    const el = tableRef.current
    if (!el) return
    const head = el.tHead?.rows?.[0]
    if (!head) return
    const next: ColumnWidths = {}
    // The header may carry leading control cells (row-select checkbox) that are not in colKeys;
    // align from the RIGHT so an extra leading cell can never shift every measurement by one.
    const cells = Array.from(head.cells)
    const offset = cells.length - colKeys.length
    colKeys.forEach((k, i) => {
      const cell = cells[i + offset]
      if (cell) next[k] = Math.round(cell.getBoundingClientRect().width)
    })
    setMeasured(prev => {
      // Keep previously measured values for columns still present — re-measuring after a resize
      // would capture the RESIZED width as the new baseline and make a reset impossible.
      const merged: ColumnWidths = {}
      for (const k of colKeys) merged[k] = prev[k] ?? next[k]
      return merged
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])

  const widthFor = useCallback(
    (k: string) => live[k] ?? saved[k] ?? measured[k],
    [live, saved, measured],
  )

  const dragTo = useCallback((k: string, px: number) => {
    setLive(w => ({ ...w, [k]: clamp(px) }))
  }, [])

  const commit = useCallback(() => {
    setLive(l => {
      if (Object.keys(l).length === 0) return l
      setSaved(s => ({ ...s, ...l }))
      return {}
    })
  }, [setSaved])

  const autoFit = useCallback((k: string, colIndex: number) => {
    const el = tableRef.current
    if (!el) return
    // Widest rendered cell in this column, including the header. scrollWidth is the content width
    // even when the cell is currently clipped by a narrower <col>.
    let widest = 0
    const head = el.tHead?.rows?.[0]
    const offset = head ? head.cells.length - colKeys.length : 0
    const headCell = head?.cells[colIndex + offset]
    if (headCell) widest = Math.max(widest, headCell.scrollWidth)
    for (const row of Array.from(el.tBodies[0]?.rows ?? [])) {
      const cell = row.cells[colIndex + offset]
      if (cell) widest = Math.max(widest, cell.scrollWidth)
    }
    if (widest > 0) {
      const px = clamp(widest + 24)   // padding + sort/filter affordances
      setLive(l => ({ ...l, [k]: px }))
      setSaved(s => ({ ...s, [k]: px }))
      setLive(l => { const { [k]: _drop, ...rest } = l; return rest })
    }
  }, [colKeys, setSaved])

  const resetWidths = useCallback(() => { setLive({}); setSaved({}) }, [setSaved])

  // Measure from the DOM as it stands right now. Same right-aligned indexing as the mount
  // measurement (leading select cell), but this one OVERWRITES rather than preserving prior
  // values — that is the whole point: the mount baseline may have come from skeleton rows.
  const captureBaseline = useCallback(() => {
    const el = tableRef.current
    const head = el?.tHead?.rows?.[0]
    if (!head) return
    const cells = Array.from(head.cells)
    const offset = cells.length - colKeys.length
    const next: ColumnWidths = {}
    colKeys.forEach((k, i) => {
      const cell = cells[i + offset]
      if (cell) next[k] = Math.round(cell.getBoundingClientRect().width)
    })
    if (Object.keys(next).length > 0) setMeasured(next)
  }, [colKeys])

  // ===================================================================================
  // HOTFIX 2026-08-09 — resizing is INERT until a user deliberately resizes.
  //
  // Shipped broken: switching every table to `table-layout: fixed` on mount overlapped
  // columns in production (date text sitting on top of the comment column). Two causes,
  // both only reachable under fixed layout:
  //   1. NOTHING CLIPS. Under fixed layout a cell wider than its column spills over its
  //      neighbour; under `auto` the column simply grew. No td/th sets overflow:hidden.
  //   2. The baseline is measured from whatever is on screen when the column set first
  //      resolves — on Prescriptions that is SKELETON rows, which are far narrower than
  //      real content, so every subsequent row overflows its column.
  //
  // Gating on `hasCustomWidths` means anyone who has never dragged gets byte-identical
  // rendering to before the feature (auto layout, no <col> widths, table keeps its own
  // minWidth). The proper fix — clip cells, and measure only once real rows exist — lands
  // separately with a spec that asserts content stays INSIDE its cell. The existing specs
  // only asserted that widths change on drag, which is why 23/23 passed on broken code.
  // ===================================================================================
  const userSized = Object.keys(saved).length > 0 || Object.keys(live).length > 0
  const ready = userSized && Object.keys(measured).length > 0
  const totalWidth = ready
    ? colKeys.reduce((sum, k) => sum + (widthFor(k) ?? 0), 0) || undefined
    : undefined

  return {
    tableRef,
    widthFor: ready ? widthFor : () => undefined,
    tableLayout: ready ? 'fixed' : 'auto',
    dragTo,
    commit,
    autoFit,
    captureBaseline,
    tableClass: ready ? 'cols-fixed' : '',
    totalWidth,
    hasCustomWidths: Object.keys(saved).length > 0,
    resetWidths,
  }
}

/**
 * The drag affordance itself — an absolutely-positioned strip on the header cell's right edge.
 *
 * Every pointer event stops propagation: the header cell's onClick SORTS, and a drag that also
 * sorted would be unusable (the design calls this out explicitly). The parent <th> needs
 * `position: relative` for the absolute placement to land on its edge.
 */
export function ColumnResizeHandle({ colKey, colIndex, api, label }: {
  colKey: string
  colIndex: number
  api: ColumnResizeApi
  label: string
}) {
  const startX = useRef(0)
  const startW = useRef(0)
  const [dragging, setDragging] = useState(false)

  const onPointerDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const th = (e.currentTarget.parentElement as HTMLElement | null)
    startX.current = e.clientX
    // Re-measure EVERY column from the current layout before the first drag switches the table
    // to fixed. Without this the drag would engage fixed layout using whatever widths were
    // captured at mount — on a list that mounts with skeleton rows, far too narrow.
    api.captureBaseline()
    startW.current = api.widthFor(colKey) ?? th?.getBoundingClientRect().width ?? MIN_COL_PX
    setDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!dragging) return
    e.stopPropagation()
    api.dragTo(colKey, startW.current + (e.clientX - startX.current))
  }

  const end = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!dragging) return
    e.stopPropagation()
    setDragging(false)
    try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* already released */ }
    api.commit()
  }

  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label} column`}
      title="Drag to resize · double-click to fit"
      data-testid={`resize-${colKey}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={end}
      onPointerCancel={end}
      // The header cell sorts on click/keydown — a resize must never sort.
      onClick={e => { e.stopPropagation() }}
      onKeyDown={e => e.stopPropagation()}
      onDoubleClick={e => { e.preventDefault(); e.stopPropagation(); api.autoFit(colKey, colIndex) }}
      style={{
        position: 'absolute', top: 0, right: 0, height: '100%', width: 9,
        cursor: 'col-resize', userSelect: 'none', touchAction: 'none',
        // Invisible until hovered/dragged: the grid should not look like a spreadsheet at rest.
        borderRight: dragging ? '2px solid var(--color-primary)' : '2px solid transparent',
      }}
      onMouseEnter={e => { if (!dragging) e.currentTarget.style.borderRight = '2px solid var(--color-border)' }}
      onMouseLeave={e => { if (!dragging) e.currentTarget.style.borderRight = '2px solid transparent' }}
    />
  )
}
